import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Tenant } from '@org/interfaces';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private apiUrl = `${environment.apiUrl}/api/tenants`;
  private readonly assignedToActiveKey = 'assignedActive';
  private tenantsSubject = new BehaviorSubject<Tenant[]>([]);
  public tenants$ = this.tenantsSubject.asObservable();
  private totaltenantsSubject = new BehaviorSubject<number>(0);
  public totaltenants$ = this.totaltenantsSubject.asObservable();
  private userAssignedToSubject = new BehaviorSubject<boolean>(false);
  public userAssignedTo$ = this.userAssignedToSubject.asObservable();
  
  

  constructor(private http: HttpClient, private authService: AuthService) {}

  // Method to create a new tenant and admin user
  createTenant(tenantData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/create`, tenantData)
      .pipe(
        catchError(this.handleError)
      );
  }

  addLocationToCorp(tenantData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/corporate/${tenantData._id}/add-location`, tenantData)
    .pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Get current user's tenant (from token)
  getTenant(): Observable<any> {
    return this.http.get(`${this.apiUrl}/tenant`)
    .pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Update current user's tenant (from token) - removed tenantId parameter
  updateTenant(tenantFormData: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/tenant`, tenantFormData)
    .pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Delete current user's tenant (from token) - removed tenantId parameter
  deleteTenant(): Observable<any> {
    return this.http.delete(`${this.apiUrl}/tenant`)
    .pipe(
      catchError(this.handleError)
    );
  }

  
  getCorporateTenants(): void {
    this.http.get(`${this.apiUrl}/corporate-tenants`).subscribe({
      next: (data: any) => {
        this.tenantsSubject.next(data.tenants);
        this.totaltenantsSubject.next(data.count);
      },
      error: (error) => {
        catchError(this.handleError)
      }
    });
  }

  // Updated: Get locations for current user's tenant (from token) - removed parentTenantId parameter
  getLocationsByCorporation(): Observable<any> {
    return this.http.get(`${this.apiUrl}/locations`).pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Get roles for current user's tenant (from token) - removed tenantId parameter
  getTenantRoles(): Observable<any[]> {
    const fallbackRoles = [
      { value: 'admin', name: 'Admin' },
      { value: 'user', name: 'User' }
    ];
  
    return this.http.get<any[]>(`${this.apiUrl}/roles`).pipe(
      map((roles: any[]) => {
        // Check if the returned array is empty, return fallbackRoles if true
        return roles; //.length > 0 ? roles : fallbackRoles;
      }),
      catchError((error) => {
        console.error('Error fetching tenant roles, using fallback:', error);
        return of(fallbackRoles); // Fallback to local array if there's an error
      })
    );
  }

  getRolePC_EST_Count(): Observable<any> {
    return this.http.get(`${this.apiUrl}/count/est-pc`).pipe(
      catchError(this.handleError)
    );
  }

  setAssignedToActiveByUser(): Observable<any> { 
    return this.http.put(`${this.apiUrl}/assignto/user/activate`, {}).pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Fixed endpoint path to match backend
  setAssignedToActiveForTenant(isAssignedActive: boolean): Observable<any> { 
    return this.http.put(`${this.apiUrl}/assignto/activate`, {status: isAssignedActive}).pipe(
      catchError(this.handleError)
    );
  }
  
  getUsersAssignToAccess(): void {
    this.http.get(`${this.apiUrl}/hasassignedto`).subscribe({
      next: (data: any) => {
        this.userAssignedToSubject.next(data.status);
      },
      error: () => {
        this.userAssignedToSubject.next(false);
      }
    });
  }

  setAssignedToStatus(isEnabled: boolean): void {
    localStorage.setItem(this.assignedToActiveKey, JSON.stringify(isEnabled));
  }

  isAssignedToEnabled(): boolean {
    const savedSetting = localStorage.getItem(this.assignedToActiveKey);
    return savedSetting !== null ? JSON.parse(savedSetting) : false;  // Default to true
  }

  getAssociatedVendors(service: string): Observable<any> { 
    return this.http.get(`${this.apiUrl}/connected-vendors/${service}`).pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Removed tenantId parameter since it's now from token
  setOrgVendorConnection(): Observable<any> {
    return this.http.post(`${this.apiUrl}/setupOrganizationEntry`, {}).pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Get current user's tenant details (from token) - removed tenantId parameter
  getShopTenantDetails(): Observable<any> { 
    return this.http.get(`${this.apiUrl}/tenant`).pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Get parent tenant ID for current user's tenant (from token)
  getParentTenantId(): Observable<any> { 
    return this.http.get(`${this.apiUrl}/parent-tenant`).pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Get tenant settings for current user's tenant (from token)
  getTenantSettings(): Observable<any> { 
    return this.http.get(`${this.apiUrl}/settings`).pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Update tenant settings for current user's tenant (from token)
  updateTenantSettings(settings: any): Observable<any> { 
    return this.http.put(`${this.apiUrl}/settings`, settings).pipe(
      catchError(this.handleError)
    );
  }

  // Updated: Update configuration settings for current user's tenant (from token)
  updateConfigurationSettings(settings: any): Observable<any> { 
    return this.http.put(`${this.apiUrl}/configuration-settings`, settings).pipe(
      catchError(this.handleError)
    );
  }

  // Handle errors
  private handleError(error: any) {
    console.error('An error occurred:', error);
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }
  

}
